import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Tag as TagIcon, Plus, Edit2, Trash2, Hash, Search, LayoutGrid, List, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useTags } from "@/hooks/useTags";
import { tagsService } from "@/services/tagsService";
import type { Tag } from "@shared/schema";

interface TagWithUsage extends Tag {
  usageCount: number;
}

type ViewMode = "grid" | "list";

const TAG_COLORS = [
  { label: "Blue", value: "#3b82f6" },
  { label: "Green", value: "#10b981" },
  { label: "Yellow", value: "#f59e0b" },
  { label: "Red", value: "#ef4444" },
  { label: "Purple", value: "#8b5cf6" },
  { label: "Pink", value: "#ec4899" },
  { label: "Gray", value: "#6b7280" },
  { label: "Teal", value: "#14b8a6" },
  { label: "Indigo", value: "#6366f1" },
  { label: "Orange", value: "#f97316" },
];

const TAG_CATEGORIES = ["Work", "Personal", "Research", "Ideas", "Projects", "Learning"];

export default function TagManagement() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
// form state
  const [formData, setFormData] = useState({
    name: "",
    color: "",
    description: "",
    category: "",
  });
// fetch tags
  const { data: tags = [], isLoading } = useTags();

  const filteredTags = tags.filter((tag) => {
    const matchesSearch =
      searchQuery === "" ||
      tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tag.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      filterCategory === "all" || tag.category === filterCategory;

    return matchesSearch && matchesCategory;
  });

  const uniqueCategories = Array.from(
    new Set(tags.map((t) => t.category).filter(Boolean))
  ) as string[];

  const createTagMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return tagsService.createTag({
        name: data.name,
        color: data.color || null,
        description: data.description || null,
        category: data.category || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tags"] });
      setIsCreateDialogOpen(false);
      resetForm();
      toast({
        title: "Tag created",
        description: "Your tag has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create tag",
        variant: "destructive",
      });
    },
  });

  const deleteTagMutation = useMutation({
    mutationFn: async (id: string) => {
      const ok = await tagsService.deleteTag(id);
      if (!ok) throw new Error("Tag not found");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tags"] });
      toast({
        title: "Tag deleted",
        description: "Your tag has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete tag",
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      color: "",
      description: "",
      category: "",
    });
  };

  const handleCreateTag = () => {
    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Tag name is required",
        variant: "destructive",
      });
      return;
    }
    createTagMutation.mutate(formData);
  };

  const handleEdit = (tag: TagWithUsage) => {
    setLocation(`/tags/${tag.id}/edit`);
  };

  const handleOpenCreateDialog = () => {
    resetForm();
    setIsCreateDialogOpen(true);
  };

  const getColorLabel = (value: string | null | undefined) => {
    if (!value) return null;
    return TAG_COLORS.find(c => c.value === value)?.label || value;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-muted-foreground">Loading tags...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold" data-testid="text-page-title">Tag Management</h1>
          <p className="text-muted-foreground">
            Manage tags across threads, spaces, and knowledge items
          </p>
        </div>
        <Button
          onClick={handleOpenCreateDialog}
          data-testid="button-create-tag"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Tag
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="input-search-tags"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[180px]" data-testid="select-filter-category">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {uniqueCategories.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-1 border rounded-md p-1">
          <Button
            variant={viewMode === "list" ? "secondary" : "ghost"}
            size="icon"
            onClick={() => setViewMode("list")}
            data-testid="button-view-list"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "grid" ? "secondary" : "ghost"}
            size="icon"
            onClick={() => setViewMode("grid")}
            data-testid="button-view-grid"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {filteredTags.length === 0 ? (
        <Card className="p-12 text-center">
          <TagIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
          <h3 className="text-lg font-semibold mb-2">No tags found</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {searchQuery || filterCategory !== "all"
              ? "Try adjusting your search or filters"
              : "Create your first tag to get started"}
          </p>
          {!searchQuery && filterCategory === "all" && (
            <Button onClick={handleOpenCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Create Tag
            </Button>
          )}
        </Card>
      ) : viewMode === "list" ? (
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">Tag</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Category</th>
                  <th className="text-left p-3 font-medium hidden lg:table-cell">Description</th>
                  <th className="text-left p-3 font-medium">Usage</th>
                  <th className="text-right p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredTags.map((tag) => (
                  <tr key={tag.id} className="hover:bg-muted/30" data-testid={`row-tag-${tag.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Hash
                          className="h-5 w-5 flex-shrink-0"
                          style={{ color: tag.color || "currentColor" }}
                        />
                        <span className="font-medium">{tag.name}</span>
                        {tag.color && (
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0" 
                            style={{ backgroundColor: tag.color }}
                            title={getColorLabel(tag.color) || undefined}
                          />
                        )}
                      </div>
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      {tag.category ? (
                        <Badge variant="outline" className="text-xs">
                          <FolderOpen className="h-3 w-3 mr-1" />
                          {tag.category}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-3 hidden lg:table-cell max-w-xs">
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {tag.description || "-"}
                      </p>
                    </td>
                    <td className="p-3">
                      <Badge variant="secondary" className="text-xs">
                        {tag.usageCount} {tag.usageCount === 1 ? "use" : "uses"}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(tag)}
                          data-testid={`button-edit-${tag.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteTagMutation.mutate(tag.id)}
                          data-testid={`button-delete-${tag.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTags.map((tag) => (
            <Card key={tag.id} className="hover-elevate" data-testid={`card-tag-${tag.id}`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 min-w-0">
                    <Hash
                      className="h-5 w-5 flex-shrink-0"
                      style={{ color: tag.color || "currentColor" }}
                    />
                    <span className="truncate">{tag.name}</span>
                  </span>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(tag)}
                      data-testid={`button-edit-${tag.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteTagMutation.mutate(tag.id)}
                      data-testid={`button-delete-${tag.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardTitle>
                <CardDescription className="flex items-center gap-2 flex-wrap">
                  {tag.category && (
                    <Badge variant="outline" className="text-xs">
                      <FolderOpen className="h-3 w-3 mr-1" />
                      {tag.category}
                    </Badge>
                  )}
                  <Badge variant="secondary" className="text-xs">
                    {tag.usageCount} {tag.usageCount === 1 ? "use" : "uses"}
                  </Badge>
                </CardDescription>
              </CardHeader>
              {tag.description && (
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {tag.description}
                  </p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent data-testid="dialog-create-tag">
          <DialogHeader>
            <DialogTitle>Create New Tag</DialogTitle>
            <DialogDescription>
              Create a new tag to organize your content
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter tag name"
                data-testid="input-tag-name"
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {TAG_COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${
                      formData.color === color.value 
                        ? "border-foreground ring-2 ring-offset-2 ring-foreground" 
                        : "border-transparent"
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                    data-testid={`create-color-swatch-${color.label.toLowerCase()}`}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, color: "" })}
                  className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 flex items-center justify-center bg-muted ${
                    !formData.color 
                      ? "border-foreground ring-2 ring-offset-2 ring-foreground" 
                      : "border-border"
                  }`}
                  title="No color"
                  data-testid="create-color-swatch-none"
                >
                  <span className="text-xs text-muted-foreground">—</span>
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Select 
                value={formData.category || "none"} 
                onValueChange={(value) => setFormData({ ...formData, category: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="select-tag-category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Category</SelectItem>
                  {TAG_CATEGORIES.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                data-testid="input-tag-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateTag}
              disabled={createTagMutation.isPending}
              data-testid="button-submit-create-tag"
            >
              Create Tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
